import React, { useEffect, useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  Save,
  X,
  CreditCard,
  DollarSign,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Package
} from 'lucide-react';
import { creditNotesAPI, suppliersAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { productsAPI } from '../services/api';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';
import AddProductForm from '../components/forms/AddProductForm';

// Updated form to use supplier_id instead of purchase_id
const defaultForm = {
  supplier_id: '',
  amount: '',
  date: '',
  status: 'pending',
  remarque: '',
  numero_achat: '',
  items: [], // Array of { product_id, quantity, price }
};

const AchatsAvoirs = () => {
  // Move all useState calls to the top of the component, before any logic or conditionals
  const [creditNotes, setCreditNotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [itemForm, setItemForm] = useState({ product_id: '', quantity: 1, price: '' });
  const [editIdx, setEditIdx] = useState(null);
  const [itemError, setItemError] = useState('');

  // Move these to the top of the component, after the other useState hooks
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEditIdx, setModalEditIdx] = useState(null);
  const [modalForm, setModalForm] = useState({ product_id: '', quantity: 1, price: '' });
  const [modalError, setModalError] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isViewMode, setIsViewMode] = useState(false);
  
  // Add product modal state
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // Handle product creation success
  const handleProductCreated = async (newProduct) => {
    try {
      // Refresh products list
      const response = await productsAPI.getProducts();
      setProducts(response.data);
      // Close the modal
      setShowAddProductModal(false);
      // Show success message
      toast.success('Produit ajouté avec succès');
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };

  // 2. Helper to add item
  const addItem = (item) => setForm(f => ({ ...f, items: [...f.items, item] }));
  const updateItem = (idx, newItem) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? newItem : it) }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // 1. Add helpers for product name, sale price, and total
  const getProductName = (id) => {
    const p = products.find(pr => pr.id === id);
    return p ? p.name : id;
  };
  const getProductSalePrice = (id) => {
    const p = products.find(pr => pr.id === id);
    return p ? p.sale_price : 0;
  };
  const getProductPurchasePrice = (id) => {
    const p = products.find(pr => pr.id === id);
    return p ? p.purchase_price : 0;
  };
  const getItemTotal = (item) => {
    const price = item.price || getProductSalePrice(item.product_id);
    return price * (item.quantity || 1);
  };

  // Fetch credit notes
  const fetchCreditNotes = async () => {
    setLoading(true);
    try {
      const res = await creditNotesAPI.getCreditNotes();
      setCreditNotes(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des avoirs');
    }
    setLoading(false);
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      const res = await suppliersAPI.getSuppliers();
      setSuppliers(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des fournisseurs');
    }
  };

  // Filter products based on search term
  const filterProducts = (searchTerm) => {
    if (!searchTerm) {
      setFilteredProducts(products);
      return;
    }
    const filtered = products.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.brand?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  useEffect(() => {
    fetchCreditNotes();
    fetchSuppliers();
    productsAPI.getProducts().then(res => {
      setProducts(res.data);
      setFilteredProducts(res.data);
    }).catch(() => {
      setProducts([]);
      setFilteredProducts([]);
    });
  }, []);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitLoading(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        amount: form.amount,
        date: form.date,
        status: form.status,
        remarque: form.remarque,
        numero_achat: form.numero_achat,
        items: form.items.length > 0 ? form.items.map(item => ({
          product_id: item.product_id,
          item_type: 'accessoire', // Default value for backend compatibility
          side: 'none', // Default value for backend compatibility
          quantity: item.quantity,
          price: item.price
        })) : [] // Empty array if no products selected
      };

      if (editingId) {
        await creditNotesAPI.updateCreditNote(editingId, payload);
        toast.success(`Avoir modifié${form.items.length > 0 ? ' avec produits' : ' sans produits'}`);
      } else {
        await creditNotesAPI.createCreditNote(payload);
        toast.success(`Avoir créé${form.items.length > 0 ? ' avec produits' : ' sans produits'}`);
      }

      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      setIsViewMode(false);
      fetchCreditNotes();
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    }
    setSubmitLoading(false);
  };

  // 5. On edit, load items for the credit note (fetch from backend if needed)
  const handleEdit = async (creditNote) => {
    try {
      // Fetch the credit note with its items from the backend
      const response = await creditNotesAPI.getCreditNote(creditNote.id);
      const creditNoteWithItems = response.data;
      
      setForm({
        supplier_id: creditNoteWithItems.supplier_id,
        amount: creditNoteWithItems.amount,
        date: creditNoteWithItems.date ? creditNoteWithItems.date.slice(0, 10) : '',
        status: creditNoteWithItems.status,
        remarque: creditNoteWithItems.remarque || '',
        numero_achat: creditNoteWithItems.numero_achat || '',
        items: creditNoteWithItems.items || [], // Use the items from the API response
      });
      setEditingId(creditNote.id);
      setIsViewMode(false);
      setShowForm(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'avoir');
      console.error('Error loading credit note:', error);
    }
  };

  // Handle view (read-only mode)
  const handleView = async (creditNote) => {
    try {
      // Fetch the credit note with its items from the backend
      const response = await creditNotesAPI.getCreditNote(creditNote.id);
      const creditNoteWithItems = response.data;
      
      setForm({
        supplier_id: creditNoteWithItems.supplier_id,
        amount: creditNoteWithItems.amount,
        date: creditNoteWithItems.date ? creditNoteWithItems.date.slice(0, 10) : '',
        status: creditNoteWithItems.status,
        remarque: creditNoteWithItems.remarque || '',
        numero_achat: creditNoteWithItems.numero_achat || '',
        items: creditNoteWithItems.items || [], // Use the items from the API response
      });
      setEditingId(creditNote.id);
      setIsViewMode(true);
      setShowForm(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'avoir');
      console.error('Error loading credit note:', error);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet avoir ?')) return;
    try {
      await creditNotesAPI.deleteCreditNote(id);
      toast.success('Avoir supprimé');
      fetchCreditNotes();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setIsViewMode(false);
    setShowForm(true);
  };

  // Helper to get supplier info
  const getSupplierInfo = (supplierId) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    return supplier ? supplier.name : supplierId;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'En attente' },
      approved: { variant: 'success', label: 'Approuvé' },
      rejected: { variant: 'danger', label: 'Rejeté' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <ModernBadge variant={config.variant}>{config.label}</ModernBadge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const filteredCreditNotes = creditNotes.filter(creditNote => {
    const supplierName = getSupplierInfo(creditNote.supplier_id);
    return String(supplierName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(creditNote.amount || '').includes(searchTerm) ||
           String(creditNote.date || '').includes(searchTerm) ||
           String(creditNote.status || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const tableColumns = [
    {
      key: 'supplier_id',
      label: 'Fournisseur',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <CreditCard className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{getSupplierInfo(value)}</div>
          </div>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Montant',
      render: (value) => (
        <span className="font-semibold text-success-600">
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'date',
      label: 'Date',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'numero_achat',
      label: 'Numéro d\'achat',
      render: (value) => (
        <div className="flex items-center">
          <span className="text-sm font-medium text-neutral-700">
            {value || '-'}
          </span>
        </div>
      )
    },
    {
      key: 'remarque',
      label: 'Remarque',
      render: (value) => (
        <div className="flex items-center">
          <span className="text-sm text-neutral-600 max-w-xs truncate" title={value}>
            {value || '-'}
          </span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, creditNote) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleView(creditNote)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(creditNote)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(creditNote.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalAmount = creditNotes.reduce((sum, note) => sum + (Number(note.amount) || 0), 0);
  const pendingNotes = creditNotes.filter(n => n.status === 'pending').length;
  const approvedNotes = creditNotes.filter(n => n.status === 'approved').length;
  const rejectedNotes = creditNotes.filter(n => n.status === 'rejected').length;

  // openModal, closeModal, handleModalChange, handleModalSave are defined here
  const openModal = (idx = null) => {
    setModalEditIdx(idx);
    if (idx !== null) {
      setModalForm(form.items[idx]);
    } else {
      setModalForm({ product_id: '', quantity: 1, price: '' });
    }
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalForm({ product_id: '', quantity: 1, price: '' });
    setModalEditIdx(null);
    setModalError('');
    setProductSearchTerm('');
  };
  const handleModalChange = (field, value) => {
    setModalForm(f => ({ ...f, [field]: value }));
  };
  const handleModalSave = () => {
    if (!modalForm.product_id) {
      setModalError('Veuillez sélectionner un produit');
      return;
    }
    if (!modalForm.quantity || modalForm.quantity < 1) {
      setModalError('La quantité doit être au moins 1');
      return;
    }
    if (!modalForm.price || modalForm.price <= 0) {
      setModalError('Le prix doit être positif');
      return;
    }

    if (modalEditIdx !== null) {
      updateItem(modalEditIdx, modalForm);
    } else {
      addItem(modalForm);
    }
    closeModal();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Avoirs</h1>
          <p className="text-neutral-600">Gérez les avoirs et remboursements</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des avoirs..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter un avoir
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Avoirs</p>
              <p className="text-2xl font-bold text-neutral-900">{creditNotes.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Montant Total</p>
              <p className="text-2xl font-bold text-success-600">
                {formatCurrency(totalAmount)}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">En Attente</p>
              <p className="text-2xl font-bold text-warning-600">{pendingNotes}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Approuvés</p>
              <p className="text-2xl font-bold text-info-600">{approvedNotes}</p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Credit Notes Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des avoirs</h3>
            <p className="text-sm text-neutral-600">{filteredCreditNotes.length} avoirs trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredCreditNotes}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setIsViewMode(false);
        }}
        title={isViewMode ? "Voir l'avoir" : (editingId ? "Modifier l'avoir" : "Ajouter un avoir")}
        size="full"
        className="!h-[95vh] !max-h-[95vh]"
      >
        <div className="max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Replace ModernSelect for Purchase with EntitySelectWithAdd */}
              <EntitySelectWithAdd
                entityType="supplier"
                label="Fournisseur"
                options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                value={form.supplier_id}
                onChange={value => setForm(f => ({ ...f, supplier_id: value }))}
                fetchOptions={fetchSuppliers}
                required
                showAddButton={!isViewMode}
                disabled={isViewMode}
              />

              <ModernInput
                label="Montant"
                name="amount"
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={handleChange}
                placeholder="0.00"
                icon={<DollarSign className="w-5 h-5" />}
                required
                disabled={isViewMode}
              />

              <ModernInput
                label="Date"
                name="date"
                type="date"
                value={form.date}
                onChange={handleChange}
                icon={<Calendar className="w-5 h-5" />}
                required
                disabled={isViewMode}
              />

              <ModernSelect
                label="Statut"
                name="status"
                value={form.status ? { value: form.status, label: form.status === 'pending' ? 'En attente' : form.status === 'approved' ? 'Approuvé' : 'Rejeté' } : null}
                onChange={(selectedOption) => setForm(f => ({ ...f, status: selectedOption ? selectedOption.value : '' }))}
                options={[
                  { value: 'pending', label: 'En attente' },
                  { value: 'approved', label: 'Approuvé' },
                  { value: 'rejected', label: 'Rejeté' }
                ]}
                required
                disabled={isViewMode}
              />

              <ModernInput
                label="Remarque"
                name="remarque"
                type="text"
                value={form.remarque}
                onChange={handleChange}
                placeholder="Ajoutez une remarque (optionnel)"
                icon={<AlertCircle className="w-5 h-5" />}
                disabled={isViewMode}
              />

              <ModernInput
                label="Numéro d'achat"
                name="numero_achat"
                type="text"
                value={form.numero_achat}
                onChange={handleChange}
                placeholder="Entrez le numéro d'achat (optionnel)"
                icon={<Calendar className="w-5 h-5" />}
                disabled={isViewMode}
              />
            </div>

            {/* Products Section - Optional */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Produits (Optionnel)</h3>
                  <p className="text-sm text-gray-500">Vous pouvez créer un avoir avec ou sans produits</p>
                </div>
                {!isViewMode && (
                  <ModernButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    icon={<Plus className="w-4 h-4" />}
                    onClick={() => openModal()}
                  >
                    Ajouter un produit
                  </ModernButton>
                )}
              </div>

              {form.items.length > 0 ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    {form.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div className="flex-1">
                          <p className="font-medium">{getProductName(item.product_id)}</p>
                          <p className="text-sm text-gray-600">
                            Quantité: {item.quantity} | Prix: {formatCurrency(item.price)}
                          </p>
                        </div>
                        {!isViewMode && (
                          <div className="flex items-center space-x-2">
                            <ModernButton
                              type="button"
                              variant="secondary"
                              size="sm"
                              icon={<Edit className="w-4 h-4" />}
                              onClick={() => openModal(idx)}
                            >
                              Modifier
                            </ModernButton>
                            <ModernButton
                              type="button"
                              variant="danger"
                              size="sm"
                              icon={<Trash2 className="w-4 h-4" />}
                              onClick={() => removeItem(idx)}
                            >
                              Supprimer
                            </ModernButton>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-lg font-medium">
                      Total: {formatCurrency(form.items.reduce((sum, item) => sum + getItemTotal(item), 0))}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <div className="text-gray-400 mb-2">
                    <Package className="w-8 h-8 mx-auto" />
                  </div>
                  <p className="text-gray-500 text-sm">
                    Aucun produit sélectionné
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    C'est parfait ! Vous pouvez créer l'avoir sans produits ou en ajouter si nécessaire
                  </p>
                </div>
              )}
            </div>

            {!isViewMode && (
              <div className="flex items-center justify-end space-x-3 pt-4">
                <ModernButton
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowForm(false);
                    setIsViewMode(false);
                  }}
                >
                  Annuler
                </ModernButton>
                <ModernButton
                  type="submit"
                  variant="primary"
                  icon={<Save className="w-4 h-4" />}
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
                </ModernButton>
              </div>
            )}

            {isViewMode && (
              <div className="flex items-center justify-end space-x-3 pt-4">
                <ModernButton
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowForm(false);
                    setIsViewMode(false);
                  }}
                >
                  Fermer
                </ModernButton>
              </div>
            )}
          </form>
        </div>
      </ModernModal>

      {/* Item Modal */}
      <ModernModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalEditIdx !== null ? 'Modifier le produit' : 'Ajouter un produit'}
        size="md"
      >
        <div className="space-y-4">
          {/* Product Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher un produit
            </label>
            <div className="relative">
              <ModernInput
                placeholder="Tapez pour rechercher..."
                value={productSearchTerm}
                onChange={(e) => {
                  setProductSearchTerm(e.target.value);
                  filterProducts(e.target.value);
                }}
                icon={<Search className="w-5 h-5" />}
              />
              <button
                type="button"
                onClick={() => setShowAddProductModal(true)}
                className="absolute right-2 top-2 h-8 w-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                title="Ajouter un nouveau produit"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Product Selection */}
          {productSearchTerm && (
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              {filteredProducts.map(product => (
                <div
                  key={product.id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                    modalForm.product_id === product.id ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                  onClick={() => {
                    handleModalChange('product_id', product.id);
                    handleModalChange('price', product.purchase_price || 0);
                    setProductSearchTerm(product.name);
                  }}
                >
                  <div className="font-medium">{product.name}</div>
                  <div className="text-sm text-gray-600">
                    Réf: {product.model || 'N/A'} | Prix: {formatCurrency(product.purchase_price || 0)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quantity and Price */}
          <div className="grid grid-cols-2 gap-4">
            <ModernInput
              label="Quantité"
              type="number"
              min="1"
              value={modalForm.quantity}
              onChange={(e) => handleModalChange('quantity', parseInt(e.target.value) || 1)}
              required
            />
            <ModernInput
              label="Prix unitaire"
              type="number"
              min="0"
              step="0.01"
              value={modalForm.price}
              onChange={(e) => handleModalChange('price', parseFloat(e.target.value) || 0)}
              required
            />
          </div>

          {modalError && (
            <div className="text-red-600 text-sm">{modalError}</div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={closeModal}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="button"
              variant="primary"
              onClick={handleModalSave}
            >
              {modalEditIdx !== null ? 'Modifier' : 'Ajouter'}
            </ModernButton>
          </div>
        </div>
      </ModernModal>

      {/* Add Product Modal */}
      <ModernModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        title="Ajouter un nouveau produit"
        size="lg"
      >
        <AddProductForm
          onSuccess={handleProductCreated}
          onCancel={() => setShowAddProductModal(false)}
        />
      </ModernModal>
    </div>
  );
};

export default AchatsAvoirs; 